import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from "react-intl";
import { Document, Page } from 'react-pdf';
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";

import {
  setContractSigningStep, setCustomFieldValue,
  showContractSigning,
  showSignaturePad
} from "../../../actions/actionFunctions";
import ContractSignPopup from "./contractSignPopup";
import { renderErrorPopup } from "../../../api/api-popups";
import Analytics from "../../../analytics";
import Pager from './../../common/pager';
import LoadingSpinner from "../../ui/loadingSpinner";
import { getDocumentForJob, postContractSign } from "../../../api/contract-api";
import {loaderDisable} from "../../ui/loaderView";

const options = {
  cMapUrl: 'cmaps/',
  cMapPacked: true,
};

const yExpand = 6;
let lastScrollTop = null;
// const xExpand = 10;

const mapStateToProps = state => {
  const { fabricatorState, contractSigningState, userState } = state;
  const { agreeEmailCheckbox } = state.serverState;

  return {
    agreeEmailCheckbox,
    step: contractSigningState.step,
    contractSigningVisible: contractSigningState.isVisible,
    contractSigningCallback: contractSigningState.callback,
    contractLoading: contractSigningState.contractLoading,
    contractSigningMasterDocumentUrl: fabricatorState.settings.masterContractFullUrl,
    signedDocumentUrl: contractSigningState.signedDocumentUrl || '',
    masterContractFields: updateFieldsPageOffset(
      fabricatorState.settings.masterContractFields,
      contractSigningState.invoiceNumberOfPages),
    jobDocumentUrl: contractSigningState.jobDocumentUrl,
    isSignaturePadVisible: contractSigningState.isSignaturePadVisible,
    signaturePadTag: contractSigningState.signaturePadTag,
    customerEmail: userState.userInfo.email,
    pagesCustomFieldsValue: contractSigningState.pagesCustomFieldsValue,
    fabricatorData: fabricatorState,
    contractId: contractSigningState.contractId,
  }
};

/**
 * Since we put first the invoice which has an unknown number of pages
 * we need to offset the page index for all fields, since they assume they start on page 1
 */
const updateFieldsPageOffset = (fields, pageOffset) => {
  if (!fields || !pageOffset)
    return fields;
  fields.forEach(f => {
    f.coords.forEach(c => {
      // need this to prevent double increment, since we are working with a reference to array, not its copy.
      if (c.pageUpdated)
        return;
      c.pageUpdated = true;
      c.page += pageOffset;
    })
  });

  return fields;
}

const mapDispatchToProps = dispatch => {
  return {
    hideMe: () => dispatch(showContractSigning(false)),
    setStep: step => dispatch(setContractSigningStep(step)),
    showSignaturePad: (show, tag) => dispatch(showSignaturePad(show, tag)),
    showContractSigning: show => dispatch(showContractSigning(show)),
    setFieldValue: data => dispatch(setCustomFieldValue(data))
  }
};

class ContractSigningLayer extends Component {
  state = {
    numPages: null,
    pageNumber: 1,
    pageRendered: false,
    selectedFieldIndex: -1,
    handleFieldOnBlur: true,
    jobStateDocumentUrl: "", // blob url, we need it because react-pdf library is caching the document, but we need to update it
  }

  currentPageFields = [];

  constructor(props) {
    super(props);

    this.pageRef = React.createRef();
    this.contractRef = React.createRef();

    this.debounceHandleWindowResize = debounce(() => this.forceUpdate());

    console.log('v.1.0.1')
  }

  async componentDidMount() {
    document.body.classList.add('body--apply-popup');
    window.addEventListener('resize', this.debounceHandleWindowResize);
    const response = await getDocumentForJob(null, true);
    if (response && response.data && response.data.jobDocumentUrl) {
      const responseFetch = await fetch(response.data.jobDocumentUrl + "?id=" + this.props.contractId);
      const blob = await responseFetch.blob();
      const jobDocumentUrl = URL.createObjectURL(blob);
      console.log("jobDocumentUrl", jobDocumentUrl);
      this.setState({
        jobStateDocumentUrl: jobDocumentUrl
      })
    }
    const [, scrollY] = this.getScroll();
    lastScrollTop = scrollY;
    loaderDisable();
  }

  componentWillUnmount() {
    document.body.classList.remove('body--apply-popup');
    window.removeEventListener('resize', this.debounceHandleWindowResize);
  }

  getScroll = () => {
    let sx, sy,
      c = this.contractRef,
      d = document,
      r = d.documentElement,
      b = d.body;
    sx = c.scrollLeft || r.scrollLeft || b.scrollLeft || 0;
    sy = c.scrollTop || r.scrollTop || b.scrollTop || 0;
    return [sx, sy];
  }

  handleContractOnScroll = (event) => {
    const scrollTop = event.currentTarget.scrollTop;
    let customFields = document.querySelectorAll(".contract-body__custom-field,.contract-body-indicator-signature,.contract-body__custom-text-field,.contract-body-indicator-next");

    customFields.forEach(element => {
      const lastTop = Number(element.style.top.replace("px", ""));
      const x = scrollTop - lastScrollTop;
      element.style.top = `${lastTop - x}px`;
    });

    lastScrollTop = scrollTop;
  }

  getFieldValue = (pageIndex, fieldIndex) => (this.props.pagesCustomFieldsValue[pageIndex]
    ? (
      this.props.pagesCustomFieldsValue[pageIndex][fieldIndex]
        ? this.props.pagesCustomFieldsValue[pageIndex][fieldIndex].value : "")
    : "");

  setFieldValue = (pageIndex, fieldIndex, name, value) => {
    this.props.setFieldValue({ pageIndex, fieldIndex, name, value });
  }

  renderCustomFields = () => {
    const { masterContractFields } = this.props;
    if (isEmpty(masterContractFields) || !this.state.numPages || !this.state.pageRendered || !this.pageRef) {
      return null;
    }

    let pageOffsetLeft = this.pageRef.offsetLeft;
    let pageOffsetTop = this.pageRef.offsetTop;
    const fields = [];
    this.currentPageFields = [];
    const [, scrollY] = this.getScroll();
    const fieldsCoords = [];
    let selectedFieldCoords = null;
    masterContractFields
      .filter(field => field.isEditable)
      .forEach(f => {
        f.coords
          .filter(coord => coord.page === this.state.pageNumber)
          .forEach(coord => {
            const { fieldIndexOnPage, isRequired } = coord;
            let { llx, lly, urx, ury } = coord.rectangle;
            const scaleX = this.pageRef.clientWidth / coord.pageRect.width;
            const scaleY = this.pageRef.clientHeight / coord.pageRect.height;
            const top = coord.pageRect.height - ury;
            const fieldCoords = {
              left: pageOffsetLeft + llx * scaleX,
              top: pageOffsetTop + top * scaleY - yExpand / 2 - scrollY,
              width: (urx - llx) * scaleX,
              height: ury - lly + yExpand * scaleY
            };

            const isSelected = fieldIndexOnPage === this.state.selectedFieldIndex;
            if (f.isCustom) {
              let className = 'contract-body__custom-field';
              if (fieldIndexOnPage === this.state.selectedFieldIndex) {
                className += ' contract-body__custom-field-selected';
              }
              if (!isSelected) {
                fields.push(<div className={className}
                  style={fieldCoords} key={fieldIndexOnPage}
                  onClick={() => this.handleFieldClick(fieldIndexOnPage)}>
                  <span>{this.getFieldValue(this.state.pageNumber, fieldIndexOnPage)}</span>
                </div>);

                if (isRequired)
                  fieldsCoords.push({ coords: fieldCoords, idx: fieldIndexOnPage });
              } else {
                fields.push(<input className='contract-body__custom-text-field'
                  style={fieldCoords} key={fieldIndexOnPage} autoFocus={true}
                  onBlur={() => this.handleTextFieldOnBlur(fieldIndexOnPage)}
                  onChange={e => this.handleTextFieldOnChange(e, fieldIndexOnPage, f.name)}
                  onKeyDown={e => this.handleTextFieldOnKeyDown(e)}
                  defaultValue={this.getFieldValue(this.state.pageNumber, fieldIndexOnPage)} />);
                selectedFieldCoords = { ...fieldCoords };
              }
            } else if (f.isSignature) {
              fields.push(this.renderSignatureField({
                pageIndex: this.state.pageNumber,
                fieldIndex: fieldIndexOnPage,
                coords: fieldCoords,
                fieldName: f.name
              }));
              if (isRequired)
                fieldsCoords.push({ coords: fieldCoords, idx: fieldIndexOnPage });
            } else if (f.isCheckBox) {
              fields.push(this.renderCheckBoxField({
                pageIndex: this.state.pageNumber,
                fieldIndex: fieldIndexOnPage,
                coords: fieldCoords,
                fieldName: f.name
              }))
            }
          });
      });

    const { intl } = this.props;

    if (selectedFieldCoords) {
      fields.push(this.renderNextIndicator({
        t: selectedFieldCoords.top,
        pageOffsetLeft,
        text: intl.formatMessage({ id: "contractSign.fillInText" })
      }));
    } else if (!isEmpty(fieldsCoords)) {
      let firstEmpty = 0;
      let text = intl.formatMessage({ id: "contractSign.fillInText" });
      while (firstEmpty < fieldsCoords.length && this.getFieldValue(this.state.pageNumber, fieldsCoords[firstEmpty].idx)) {
        firstEmpty++;
      }
      if (firstEmpty >= fieldsCoords.length)
        firstEmpty = fieldsCoords.length - 1;
      if (this.getFieldValue(this.state.pageNumber, fieldsCoords[firstEmpty].idx)) {
        const { page } = this.getFirstUnfilledCustomField();
        if (page !== -1)
          text = intl.formatMessage({ id: "contractSign.next" });
        else
          text = intl.formatMessage({ id: "contractSign.sign" });
      }

      fields.push(this.renderNextIndicator({ t: fieldsCoords[firstEmpty].coords.top, pageOffsetLeft, text }));
    }

    return fields;
  }

  renderCheckBoxField = ({ pageIndex, fieldIndex, fieldName, coords }) => {
    const fieldValue = this.getFieldValue(pageIndex, fieldIndex);

    const style = {
      left: coords.left,
      top: coords.top + 2
    }

    return (
      <div className={'contract-body__custom-field contract-body__custom-field-checkbox'}
        key={`checkbox_${fieldIndex}`} style={style}
        onClick={() => {
          this.setFieldValue(pageIndex, fieldIndex, fieldName, !fieldValue);
          this.forceUpdate();
        }}
      >
        {!!fieldValue && (
          <Fragment>
            <div className="contract-body__custom-field-checkbox-checked-1" />
            <div className="contract-body__custom-field-checkbox-checked-2" />
          </Fragment>
        )}
      </div>
    )
  }

  handleSignatureFieldClick = ({ pageIndex, fieldIndex, fieldName }) => {
    this.props.showSignaturePad(true, { pageIndex, fieldIndex, fieldName });
  }

  renderSignatureField = ({ pageIndex, fieldIndex, fieldName, coords }) => {
    const fieldValue = this.getFieldValue(pageIndex, fieldIndex);

    const style = {
      left: coords.left + coords.width / 2 - 20,
      top: coords.top + coords.height / 2 - 20
    }

    return (<div className={classNames('contract-body-indicator', 'contract-body-indicator-signature',
      { 'contract-body-indicator-signature-signed': !!fieldValue })}
      key={`signature_${fieldIndex}`} style={style}
      onClick={() => this.handleSignatureFieldClick({ pageIndex, fieldIndex, fieldName })}>
      <FormattedMessage id={fieldValue ? "contractSign.signed" : "contractSign.signHere"} />
    </div>)
  }

  handleNextIndicatorClick = () => {
    const { page, fieldIndex } = this.getFirstUnfilledCustomField();
    if (page === -1) {
      this.tryShowSignaturePad();
      return;
    }

    this.setState({
      pageRendered: this.state.pageNumber === page,
      pageNumber: page,
      selectedFieldIndex: fieldIndex,
    });
  }

  renderNextIndicator = ({ t, pageOffsetLeft, text }) => {
    const style = {
      left: pageOffsetLeft - 60,
      top: t
    }

    return (<div className='contract-body-indicator contract-body-indicator-next'
      style={style} key='next-indicator'
      onClick={this.handleNextIndicatorClick}>
      {text}
    </div>)
  }

  /**
   *
   * @returns {{fieldIndex: number, page: number}}
   */
  getFirstUnfilledCustomField = () => {
    const { masterContractFields } = this.props;
    // need to find first un-filled custom field and change the document page to it
    let customFieldInfo = { page: -1, fieldIndex: -1 };
    masterContractFields
      .filter(field => field.isEditable && field.isRequired)
      .forEach(field => {
        if (customFieldInfo.page !== -1) {
          return false;
        }
        field.coords.forEach(coord => {
          if (!this.props.pagesCustomFieldsValue[coord.page]) {
            customFieldInfo = { page: coord.page, fieldIndex: coord.fieldIndexOnPage };
            return false;
          }

          const obj = this.props.pagesCustomFieldsValue[coord.page][coord.fieldIndexOnPage];
          if (!obj || !obj.value) {
            customFieldInfo = { page: coord.page, fieldIndex: coord.fieldIndexOnPage };
            return false;
          }
        })
      });

    return customFieldInfo;
  }

  /**
   *
   * @param fieldIndex {int}
   */
  handleFieldClick = (fieldIndex) => {
    this.setState({ selectedFieldIndex: fieldIndex });
  }

  handleTextFieldOnBlur = () => {
    //   if (this.state.handleFieldOnBlur) {
    //     const {fieldIndex} = this.getFirstUnfilledCustomField();
    //     console.log(444, fieldIndex)
    //     if (fieldIndex === -1) {
    //       this.setState({selectedFieldIndex: -1});
    //       this.tryGoToNextPage();
    //     } else {
    //       this.setState({selectedFieldIndex: fieldIndex});
    //     }
    //   }
  }

  handleTextFieldOnChange = (e, fieldIndex, fieldName) => {
    this.setFieldValue(this.state.pageNumber, fieldIndex, fieldName, e.target.value);
  }

  handleTextFieldOnKeyDown = e => {
    if (e.keyCode === 13) {
      this.setState({ selectedFieldIndex: -1 })
    } else if (e.keyCode === 9) {
      e.preventDefault();
      const { fieldIndex } = this.getFirstUnfilledCustomField();
      if (fieldIndex === -1) {
        this.setState({ selectedFieldIndex: -1 });
        this.tryGoToNextPage();
      } else {
        this.setState({ selectedFieldIndex: fieldIndex });
      }
    }
  }

  /**
   *
   * @param page {int}
   */
  handlePageChange = page => {
    this.setState({
      pageNumber: page,
      selectedFieldIndex: -1
    })
  }

  onRenderSuccess = () => {
    this.setState({ pageRendered: true })
  }

  finishProcess = () => {
    if (this.state.pageNumber < this.state.numPages) {
      this.setState({ pageNumber: this.state.pageNumber + 1 });
      return;
    }

    const firstUnFilledCustomField = this.getFirstUnfilledCustomField();
    if (firstUnFilledCustomField.page !== -1) {
      const { intl } = this.props;
      renderErrorPopup(intl.formatMessage({ id: "contractSign.errorFieldsPopupMessage" }), "", this.finishProcess.bind(this), false, () => {
        setTimeout(() => {
          this.setState({
            pageRendered: this.state.pageNumber === firstUnFilledCustomField.page,
            pageNumber: firstUnFilledCustomField.page,
            selectedFieldIndex: firstUnFilledCustomField.fieldIndex,
          });
        }, 300);
      });
      return;
    }

    postContractSign({
      offlineRetryCallback: this.finishProcess.bind(this)
    });
    // this.props.showSignaturePad(true);
  }

  tryGoToNextPage = () => {
    const firstUnFilledCustomField = this.getFirstUnfilledCustomField();
    if (firstUnFilledCustomField.page === -1 && this.state.pageNumber < this.state.numPages) {
      this.setState({ pageNumber: this.state.pageNumber + 1 });
    }
  }

  handleSignaturePadClientSideCallback = (pngBase64) => {
    const { signaturePadTag } = this.props;
    const { pageIndex, fieldIndex, fieldName } = signaturePadTag;
    this.setFieldValue(pageIndex, fieldIndex, fieldName, pngBase64);
    this.props.showSignaturePad(false, null);
  }

  closeContractSigning = () => {
    Analytics.sendClickEvent("contract-signed-ok");
    this.props.showContractSigning(false);
    this.props.contractSigningCallback();
  }

  render() {
    if (!this.state.jobStateDocumentUrl) {
      return <div><FormattedMessage id="contractSign.loading" /></div>
    }

    const { step, customerEmail, contractLoading, agreeEmailCheckbox, fabricatorData } = this.props;
    const documentUrl = (step === 1 || step === 2)
      ? this.state.jobStateDocumentUrl : this.props.signedDocumentUrl;
    const isFinished = step === 3;
    const showEmail = isFinished && agreeEmailCheckbox && !fabricatorData.disableCustomerEmails;

    return (
      <div className={"overlay overlay--contract" + (this.props.isSignaturePadVisible ? " overlay--contract-with-popup" : "")}>
        <button className="ui-close contract-close-button" style={{ right: "40px" }} onClick={() => this.closeContractSigning()} />
        {isFinished ? (
          <Fragment>
            <div className="contract-popup__success">
              <div className="contract-success">
                <p className="contract-success__text">
                  <FormattedMessage id={showEmail ? "contractSign.contractSuccessText" : "contractSign.contractSuccessTextWithoutEmail"} />
                </p>
                {showEmail ? <p className="contract-success__email">{customerEmail}</p> : null}
                <br />
                {this.props.signedDocumentUrl ? (
                  <p className="contract-success__email">
                    <button className="ui-button2 ui-button2--green ui-button3--wide" onClick={() => window.open(this.props.signedDocumentUrl, "_blank")}>
                      <FormattedMessage id="contractSign.downloadButton" />
                    </button>
                  </p>
                ) : null}
              </div>
            </div>
            <div className="contract-popup__footer" style={{ paddingRight: "40px" }}>
              <button className="ui-button2 ui-button2--green ui-button3--wide" onClick={() => this.closeContractSigning()}>
                <FormattedMessage id="contractSign.footerCloseButton" />
              </button>
            </div>
          </Fragment>
        ) : (
          <div className="contract" ref={ref => this.contractRef = ref}
            onScroll={this.handleContractOnScroll}>
            {contractLoading ? <div className="contract-body"/> : (
              <div className="contract-body">
                <Document file={documentUrl} onLoadSuccess={pdf => this.setState({ numPages: pdf.numPages })}
                  options={options}>
                  <Page inputRef={ref => this.pageRef = ref}
                    pageNumber={this.state.pageNumber} onRenderSuccess={() => this.onRenderSuccess()}
                    scale={1.5}
                  />
                </Document>
                {this.renderCustomFields()}
              </div>
            )}
            {this.state.numPages && this.state.numPages > 1 && !contractLoading ? (
              <Pager totalPages={this.state.numPages}
                pageClick={page => {
                  this.setState({ pageNumber: page })
                }} page={this.state.pageNumber - 1} />
            ) : null}
            <div className="contract-footer">
              {this.state.numPages && !contractLoading ? (
                <button className="ui-button3 ui-button--3 ui-button3--green ui-button3--wide"
                  onClick={this.finishProcess}>
                  {(this.state.pageNumber < this.state.numPages)
                    ? <FormattedMessage id="contractSign.footerNextButton" />
                    : <FormattedMessage id="contractSign.footerFinishButton" />}
                </button>
              ) : <LoadingSpinner />}
            </div>
          </div>
        )}
        {this.props.isSignaturePadVisible
          ? <ContractSignPopup isClientSideOnly={true}
            clientSideCallback={this.handleSignaturePadClientSideCallback} />
          : null}
      </div>
    )
  }
}

ContractSigningLayer.propTypes = {
  contractSigningVisible: PropTypes.bool.isRequired,
  contractLoading: PropTypes.bool,
  contractSigningCallback: PropTypes.func,
  showSignaturePad: PropTypes.func,
  isSignaturePadVisible: PropTypes.bool,
  signaturePadTag: PropTypes.object,
  contractSigningMasterDocumentUrl: PropTypes.string,
  signedDocumentUrl: PropTypes.string.isRequired,
  hideMe: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  isTesting: PropTypes.bool,
  jobDocumentUrl: PropTypes.string,
  customerEmail: PropTypes.string,
  invoiceNumberOfPages: PropTypes.number,
  setFieldValue: PropTypes.func,
  pagesCustomFieldsValue: PropTypes.object,
  masterContractFields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    isPresent: PropTypes.bool,
    occurrencesCount: PropTypes.number,
    isRequired: PropTypes.bool,
    isCustom: PropTypes.bool,
    coords: PropTypes.arrayOf(PropTypes.shape({
      rectangle: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        llx: PropTypes.number,
        lly: PropTypes.number,
        urx: PropTypes.number,
        ury: PropTypes.number,
        isEmpty: PropTypes.bool,
      }).isRequired,
      page: PropTypes.number.isRequired,
      pageRect: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
        llx: PropTypes.number,
        lly: PropTypes.number,
        urx: PropTypes.number,
        ury: PropTypes.number,
        isEmpty: PropTypes.bool,
      }).isRequired
    }))
  }))
};

ContractSigningLayer.defaultValues = {
  isTesting: false,
  masterContractFields: []
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(ContractSigningLayer)
)
